import classNames from "classnames";
import { Entry, EntrySkeletonType } from "contentful";
import Link from "next/link";
import { useRouter } from "next/router";
import { CSSProperties, useMemo } from "react";

import EscapeNewLine from "~/components/common/escape-new-line";
import HighlightedText from "~/components/common/highlighted-text";
import MediaAsset from "~/components/common/media-asset";
import Editorials from "~/types/editorials";
import contentfulUtils from "~/utils/contentful-utils";
import { assertEditorialType, isActiveEntry } from "~/utils/editorial-utils";
import textUtils from "~/utils/text-utils";

import buttonStyles from "../../common/button.module.scss";
import Countdown from "../../common/countdown";
import styles from "./hero-panel-content.module.scss";

type Props = {
  entry: Entry<EntrySkeletonType>;
  isActive?: boolean;
  contentBelowHeader?: boolean;
  isHeroSlider?: boolean;
};

export default function PanelContent(props: Props) {
  const router = useRouter();
  const { entry } = props;
  assertEditorialType<Editorials.HeroSlide>(entry, "heroSlide");
  const fields = entry.fields;
  const customPanel = fields.customPanel;

  const inspectorMode = contentfulUtils.useInspectorMode(props.entry);

  const panelVariant = fields.panelVariant;

  const toRgba = (hex: string, alpha: number) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r} ${g} ${b} / ${alpha})`;
  };

  const panelClassName = useMemo(() => {
    const media = isActiveEntry(fields.media) ? fields.media : undefined;
    const aspectRatioMobile = media?.fields?.aspectRatioMobile ?? "unset";
    const aspectRatioDesktop = media?.fields?.aspectRatioDesktop ?? "unset";

    return classNames(
      styles.panel,
      media?.fields?.controls ? styles.increaseHeightPanel : undefined,
      aspectRatioMobile !== "unset" && !props.isHeroSlider
        ? styles.panelMarginBottomMobile
        : undefined,
      aspectRatioDesktop !== "unset" && !props.isHeroSlider
        ? styles.panelMarginBottomDesktop
        : undefined,
      fields.centerText ? styles.centerText : undefined,
      panelVariant == "black" ? styles.panelVariant : undefined,
      { [styles.centerPanel]: fields.centerPanel && !customPanel },
      { [styles.customPanel]: customPanel },
      { [styles.verticalCenter]: customPanel?.verticalAlign === "center" },
      { [styles.contentBelowHeader]: props.contentBelowHeader },
      { [styles.transparent]: customPanel?.backgroundOpacity === 0 },
    );
  }, [
    fields,
    customPanel,
    panelVariant,
    props.isHeroSlider,
    props.contentBelowHeader,
  ]);

  const cssVariables = useMemo(() => {
    if (!customPanel) {
      return {};
    }
    const grid = { total: 12, span: 4 };
    const horizontalValues = {
      left: 1,
      center: grid.total / 2 - grid.span / 2 + 1,
      right: grid.total - grid.span + 1,
    };
    const verticalValues = {
      top: "start",
      center: "center",
      bottom: "end",
    };
    return {
      "--hero-slide-cta-bg": customPanel.ctaBackground,
      "--hero-slide-cta-color": customPanel.ctaColor,
      "--hero-slide-cta-bg-hover": customPanel.ctaBackgroundHover,
      "--hero-slide-cta-color-hover": customPanel.ctaColorHover,
      "--hero-slide-cta-size": customPanel.ctaSize / 16 + "rem",
      "--hero-slide-title-color": customPanel.titleColor,
      "--hero-slide-title-size": customPanel.titleSize / 16 + "rem",
      "--hero-slide-description-color": customPanel.descriptionColor,
      "--hero-slide-description-size": customPanel.descriptionSize / 16 + "rem",
      "--hero-slide-grid-c-start":
        horizontalValues[customPanel.horizontalAlign],
      "--hero-slide-align-self": verticalValues[customPanel.verticalAlign],
      "--hero-slide-text-align": customPanel.textAlignment,
      "--hero-slide-bg": toRgba(
        customPanel.backgroundColor,
        customPanel.backgroundOpacity,
      ),
      "--hero-slide-horizontal-padding":
        customPanel.backgroundOpacity === 0 ? "0" : "1rem",
    } as CSSProperties;
  }, [customPanel]);

  if (customPanel?.hidePanel) {
    return null;
  }

  const Wrapper = ({ children }: { children: React.ReactNode }) => {
    return fields.link && isActiveEntry(fields.link) ? (
      <Link
        href={textUtils.sanitizeContentfulUrl(fields.link, router)}
        className={panelClassName}
        tabIndex={props.isActive ? 0 : -1}
        style={cssVariables}
      >
        {children}
      </Link>
    ) : (
      <div className={panelClassName} style={cssVariables}>
        {children}
      </div>
    );
  };

  return (
    <Wrapper>
      <h2
        {...inspectorMode?.getProps("title")}
        className={classNames(styles.title, {
          [styles.customTitle]: customPanel,
        })}
      >
        <HighlightedText text={fields.title} />
      </h2>
      <div
        className={classNames(styles.descriptionWrapper, {
          [styles.customDescription]: customPanel,
        })}
      >
        {fields.description && (
          <p
            {...inspectorMode?.getProps("description")}
            className={styles.description}
          >
            <EscapeNewLine text={fields.description} />
          </p>
        )}
        {fields.logo && (
          <div className={styles.mediaWrapper}>
            <MediaAsset entry={fields.logo} />
          </div>
        )}
      </div>
      {fields.link && isActiveEntry(fields.link) && (
        <div
          className={classNames(
            buttonStyles.button,
            buttonStyles.linkButton,
            panelVariant == "black"
              ? buttonStyles["secondary"]
              : buttonStyles["primary"],
            { [styles.customButton]: customPanel },
          )}
          {...inspectorMode?.getProps("text")}
        >
          {fields.link.fields.text}
        </div>
      )}
      {fields.countdown && (
        <Countdown
          entry={entry}
          date={fields.countdown}
          customStyles={{
            containerCountdown: styles.containerCountdown,
            wrapperCountdown: styles.wrapperCountdown,
            labelCountdown: styles.labelCountdown,
            numberCountdown: styles.numberCountdown,
          }}
        />
      )}
    </Wrapper>
  );
}
